import type { Diamond } from '../api/types'
import { stoneKey } from './stoneKey'

/**
 * The client-side "Sourcing list" — the tray's cart (docs/ASKA-PLUS-AGENTIC-
 * SOURCING.md §2, build slice 1). A buyer taps "＋ Add to sourcing" on a stone
 * and it lands here; the tray reads this list, groups it by supplier, and
 * "Start sourcing" (inert until the backend arms) hands it to the orchestrator.
 *
 * This is the LOCAL twin of `lib/sessionCache` favourites, with two deliberate
 * differences:
 *   · it persists in localStorage (a sourcing list is a considered basket the
 *     buyer builds over a visit — it must survive a reload, unlike the
 *     session-scoped favourite cache which only de-dupes fetches), and
 *   · it stores a compact SNAPSHOT of each stone (enough to render a tray row
 *     and group by supplier) because there is no server list to re-hydrate
 *     from while the backend is inert — the tray reads this and only this.
 *
 * Nothing here is authoritative: when the place endpoint arms, the server
 * re-reads live prices + supplier at confirm time (the accept-total handshake).
 * The snapshot is display-only. Everything degrades safely: a storage failure
 * leaves an in-memory list for the session rather than throwing into a caller.
 *
 * ⚠️ This module holds a stone snapshot including whatever the SERVER chose to
 * send for this caller (a verified buyer's `supplier` block, or nothing). It is
 * NOT a new price/supplier side-channel: it only ever stores what the buyer was
 * already shown, keyed in their own browser, and it is never read to gate a
 * price. (CLAUDE.md invariant 3 lives on the server projection, upstream.)
 */

const STORAGE_KEY = 'diaex-sourcing-list'
export const SOURCING_LIST_EVENT = 'aska:sourcing-list-changed'

/** The compact snapshot stored per stone — a tray row + supplier grouping key.
 *  A subset of `Diamond`; never the raw doc (we don't need it and it's large). */
export interface SourcingListItem {
  /** Stable identity — `stoneKey(stone)`, the tray's dedupe + remove key. */
  key: string
  stockId: string
  uniqueId?: number | null
  shape: string | null
  carat: number
  color: string | null
  clarity: string | null
  pricePerCarat: number | null
  priceUSD: number | null
  /** The listing supplier's display name if the server revealed it (verified
   *  caller), else null → the tray groups it under "Supplier pending". Only
   *  ever what the buyer was already shown. */
  supplierName: string | null
  supplierId: number | null
  /** When it was added — the tray can sort newest-first. */
  addedAt: number
}

// In-memory mirror so reads are synchronous and a storage failure still gives
// a working (session-lived) list. Loaded lazily from localStorage once.
let items: SourcingListItem[] | null = null
const subs = new Set<() => void>()

function load(): SourcingListItem[] {
  if (items) return items
  items = []
  if (typeof window === 'undefined') return items
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        items = parsed.filter(
          (i): i is SourcingListItem =>
            !!i && typeof i.key === 'string' && typeof i.stockId === 'string',
        )
      }
    }
  } catch {
    // Corrupt / unavailable — start empty (the safe direction).
    items = []
  }
  return items
}

function persist() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items ?? []))
  } catch {
    // Storage full / disabled — keep the in-memory list for this session.
  }
}

function notify() {
  for (const fn of subs) fn()
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SOURCING_LIST_EVENT))
  }
}

/** Build the stored snapshot from a full Diamond — display fields only. */
function snapshot(stone: Diamond): SourcingListItem {
  return {
    key: stoneKey(stone),
    stockId: stone.stockId,
    uniqueId: stone.uniqueId ?? null,
    shape: stone.shape,
    carat: stone.carat,
    color: stone.color,
    clarity: stone.clarity,
    pricePerCarat: stone.pricePerCarat,
    priceUSD: stone.priceUSD,
    supplierName: stone.supplier?.name?.trim() || null,
    supplierId: stone.supplierId ?? null,
    addedAt: Date.now(),
  }
}

/** Add a stone to the sourcing list (idempotent — a re-add refreshes the
 *  snapshot but never duplicates the row). */
export function addToSourcing(stone: Diamond): void {
  const list = load()
  const key = stoneKey(stone)
  const next = snapshot(stone)
  const at = list.findIndex((i) => i.key === key)
  if (at >= 0) list[at] = { ...next, addedAt: list[at].addedAt } // keep original add time
  else list.push(next)
  persist()
  notify()
}

/** Remove a stone from the list by its `stoneKey`. */
export function removeFromSourcing(key: string): void {
  const list = load()
  const at = list.findIndex((i) => i.key === key)
  if (at < 0) return
  list.splice(at, 1)
  persist()
  notify()
}

/** The full list (newest last — the order they were added). */
export function sourcingList(): SourcingListItem[] {
  return [...load()]
}

/** Is this stone (by `stoneKey`) currently in the list? */
export function isInSourcing(key: string): boolean {
  return load().some((i) => i.key === key)
}

/** How many stones are in the list — the tray badge count. */
export function sourcingCount(): number {
  return load().length
}

/** Empty the list (used after a successful place, once the backend arms). */
export function clearSourcing(): void {
  items = []
  persist()
  notify()
}

/** Subscribe to list changes (add/remove/clear). Returns an unsubscribe fn.
 *  Also refreshes on the cross-tab `storage` event so a second tab stays in
 *  sync. Mirrors sessionCache's subscribe shape. */
export function subscribeSourcing(cb: () => void): () => void {
  subs.add(cb)
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) { items = null; cb() }
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage)
  return () => {
    subs.delete(cb)
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}

/** Group the list by supplier for the tray's "grouped by supplier" view. A
 *  null supplier name (unverified caller, or the server didn't reveal one)
 *  collects under a single "Supplier pending" bucket. */
export interface SourcingGroup {
  supplierName: string | null
  items: SourcingListItem[]
}

export function sourcingGroupedBySupplier(): SourcingGroup[] {
  const list = load()
  const byName = new Map<string, SourcingGroup>()
  for (const item of list) {
    const label = item.supplierName ?? ' pending' // sort the pending bucket last
    let g = byName.get(label)
    if (!g) {
      g = { supplierName: item.supplierName, items: [] }
      byName.set(label, g)
    }
    g.items.push(item)
  }
  return [...byName.values()].sort((a, b) => {
    // Named suppliers first (alpha), the "pending" bucket last.
    if (a.supplierName == null) return 1
    if (b.supplierName == null) return -1
    return a.supplierName.localeCompare(b.supplierName)
  })
}

/** Test/sign-out hook — drop the list from memory (localStorage untouched, so
 *  a real user's basket survives; tests reset explicitly). */
export function resetSourcingListMemory(): void {
  items = null
  subs.clear()
}

/**
 * A seed want for `POST /sourcing/want-lists` (docs/ASKA-PLUS-AGENTIC-SOURCING.md
 * §2.1) — the client shape the server's PlaceSchema accepts, built from a list
 * item. Every list item is a KNOWN catalog stone, so it becomes a `specific`
 * want (source THIS stone). The `spec` is a minimal filter that identifies the
 * stone family (the server re-resolves the real stone by stockId), and the limit
 * is the price the buyer was shown (a ceiling — the agent sources at/under it);
 * a locked (price-null) item gets a permissive high ceiling so the want still
 * places (the accept-total handshake is the real price gate server-side). */
export interface WantSeed {
  spec: Record<string, unknown>
  limitBasis: 'ppc' | 'total'
  limitValue: number
  wantKind: 'specific' | 'spec'
  specificStockId: string
  sourceMode: 'rfq'
}

function seedFromItem(item: SourcingListItem): WantSeed {
  const spec: Record<string, unknown> = {}
  if (item.shape) spec.shape = [item.shape]
  if (item.color) spec.color = [item.color]
  if (item.clarity) spec.clarity = [item.clarity]
  if (Number.isFinite(item.carat) && item.carat > 0) { spec.caratMin = item.carat; spec.caratMax = item.carat }
  // A shown per-carat is the natural ceiling; fall back to a permissive cap for a
  // locked (price-null) row so the want still places (server re-prices at accept).
  const ppc = typeof item.pricePerCarat === 'number' && item.pricePerCarat > 0 ? item.pricePerCarat : 100_000
  return {
    spec,
    limitBasis: 'ppc',
    limitValue: ppc,
    wantKind: 'specific',
    specificStockId: item.stockId,
    sourceMode: 'rfq',
  }
}

/** Build the seed wants for the whole current list — what "Start sourcing" sends. */
export function sourcingSeeds(): WantSeed[] {
  return load().map(seedFromItem)
}
